import { atom } from 'infrastructure/state/jotai.wrapper'
import { KiddomStandard } from 'components/standards/types'
import {
  SheetNode,
  SheetImportError,
  SyncResult,
  CascadeData,
  ImportedSheetData,
  SkillsListDifference,
} from './types'
import { findSkillsListDifferences } from './diff_utils'
import { cascadeSkills } from './cascade_utils'

// Standard IDs extracted from tree (do not interpret) - stored as Set for uniqueness
export const standardIdsAtom = atom<Set<number>>(new Set<number>())

// Map of standard_id -> KiddomStandard
// Only includes standards that were successfully fetched from the API
export const standardIdMapAtom = atom<Record<number, KiddomStandard>>({})

// Set of standard IDs that were requested but not returned by the API
export const idsNotFoundAtom = atom<Set<number>>(new Set<number>())

// Consolidated imported sheet data including nodes and tree mappings
export const importedSheetDataAtom = atom<ImportedSheetData>(null)

// Errors found during sheet import validation
export const sheetImportErrorsAtom = atom<SheetImportError[]>([])

// Original tree nodes data extracted from the live tree as a list of SheetNodes
export const nodesFromTreeAtom = atom<SheetNode[]>([])

// Map of node.id -> parent.id from tree structure (for hierarchy building)
// Note: This is now included in importedSheetDataAtom, but kept for tree scanning
export const treeNodeIdToParentIdMapAtom = atom<Map<string, string>>(new Map())

// Google Sheet ID entered by the user
export const sheetIdAtom = atom<string>('')

// Sync results from the sync operation
export const syncResultsAtom = atom<SyncResult[]>([])

// Sync errors from the sync operation
export const syncErrorsAtom = atom<string[]>([])

// Refresh counter: increments when sync starts, resets to 0 when reset is pressed
// Counter > 0 indicates sync has started (buttons disabled)
// Counter = 0 triggers flow component reset
export const refreshCounterAtom = atom<number>(0)

// Cascade UI state atoms
export const cascadeFromAtom = atom<string>('')
export const cascadeToAtom = atom<string>('')
export const cascadeTitleAtom = atom<string>('')
export const cascadeModeAtom = atom<string>('Off')

// Cascade data calculated from cascade UI atoms and imported sheet data
// This is a derived atom that automatically recalculates when dependencies change
export const cascadeDataAtom = atom<CascadeData>((get) => {
  const importedSheetData = get(importedSheetDataAtom)
  const from = get(cascadeFromAtom)
  const to = get(cascadeToAtom)
  const title = get(cascadeTitleAtom)
  const mode = get(cascadeModeAtom)

  return cascadeSkills(from, to, title, mode, importedSheetData)
})

// Sync differences calculated from imported sheet data, tree nodes, and cascade data
// This is a derived atom that automatically recalculates when dependencies change
export const syncDifferencesAtom = atom<SkillsListDifference[]>((get) => {
  const importedSheetData = get(importedSheetDataAtom)
  const nodesFromTree = get(nodesFromTreeAtom)
  const cascadeData = get(cascadeDataAtom)

  return findSkillsListDifferences(
    importedSheetData,
    nodesFromTree,
    cascadeData,
  )
})
